/**
 * Provisión masiva: otorga (o revoca) a un contador del equipo acceso a N
 * empresas cliente con un rol, en bloque. Mapea a POST /firm/provision/.
 *
 * El usuario a provisionar se elige del staff del estudio; las empresas, de la
 * cartera activa. Idempotente en el backend (los schemas fuera del estudio se
 * reportan como "omitidos").
 */
import { useMemo, useState } from 'react'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { Button } from '@/components/Button'
import { getApiErrorMessage } from '@/lib/apiClient'
import { ROLE_NAMES, type RoleType } from '@/types'
import { useRoles } from '@/modules/users/hooks/useUsers'
import {
  useFirmClients, useFirmStaff, useProvisionStaff,
} from '@/modules/firm/hooks/useFirm'

interface Props {
  open: boolean
  onClose: () => void
  toast: { success: (m: string) => void; error: (m: string) => void; warning: (m: string) => void }
}

const fieldCls =
  'w-full px-3 py-2 text-sm rounded-lg bg-surface border border-divider text-content focus:outline-none focus:ring-2 focus:ring-focus-ring'

export function ProvisionModal({ open, onClose, toast }: Props) {
  const { data: staff } = useFirmStaff()
  const { data: clients } = useFirmClients()
  const { data: roles } = useRoles()
  const provision = useProvisionStaff()

  const [userId, setUserId] = useState('')
  const [role, setRole] = useState('')
  const [revoke, setRevoke] = useState(false)
  const [selected, setSelected] = useState<string[]>([])

  const activeClients = useMemo(() => (clients ?? []).filter((c) => c.is_active), [clients])
  const activeStaff = useMemo(() => (staff ?? []).filter((m) => m.status === 'accepted'), [staff])

  const reset = () => {
    setUserId(''); setRole(''); setRevoke(false); setSelected([])
  }

  const toggle = (schema: string) => {
    setSelected((prev) =>
      prev.includes(schema) ? prev.filter((s) => s !== schema) : [...prev, schema],
    )
  }

  const allSelected = activeClients.length > 0 && selected.length === activeClients.length
  const toggleAll = () => setSelected(allSelected ? [] : activeClients.map((c) => c.tenant_schema))

  const canSubmit = !!userId && !!role && selected.length > 0 && !provision.isPending

  const handleSubmit = async () => {
    if (!canSubmit) return
    try {
      const res = await provision.mutateAsync({
        user_id: userId, role, tenant_schemas: selected, revoke,
      })
      const done = revoke ? res.revoked.length : res.provisioned.length
      const skipped = res.skipped.length
      toast.success(
        `${revoke ? 'Acceso revocado en' : 'Acceso otorgado en'} ${done} empresa(s)` +
        (skipped ? ` · ${skipped} omitida(s)` : ''),
      )
      reset()
      onClose()
    } catch (err) {
      toast.error(getApiErrorMessage(err))
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Provisión masiva de acceso"
      size="md"
      actions={
        <>
          <ModalCancelButton onClick={onClose} />
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            isPending={provision.isPending}
            variant={revoke ? 'danger' : 'primary'}
            size="lg"
          >
            {revoke ? 'Revocar acceso' : 'Otorgar acceso'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="provisionmodal-contador" className="block text-sm font-medium text-content mb-1">Contador</label>
            <select id="provisionmodal-contador" value={userId} onChange={(e) => setUserId(e.target.value)} className={fieldCls}>
              <option value="">Selecciona un contador…</option>
              {activeStaff.map((m) => (
                <option key={m.id} value={m.user}>
                  {m.user_detail.full_name || m.user_detail.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="provisionmodal-rol-en-las-empresas" className="block text-sm font-medium text-content mb-1">Rol en las empresas</label>
            <select id="provisionmodal-rol-en-las-empresas" value={role} onChange={(e) => setRole(e.target.value)} className={fieldCls}>
              <option value="">Selecciona un rol…</option>
              {(roles ?? []).map((r) => (
                <option key={r.id} value={r.role_type}>
                  {ROLE_NAMES[r.role_type as RoleType] ?? r.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-content cursor-pointer">
          <input type="checkbox" checked={revoke} onChange={(e) => setRevoke(e.target.checked)}
            className="rounded border-divider text-primary focus:ring-focus-ring" />
          Revocar acceso en vez de otorgarlo
        </label>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-content">Empresas ({selected.length})</label>
            {activeClients.length > 0 && (
              <button type="button" onClick={toggleAll} className="text-xs text-primary hover:underline">
                {allSelected ? 'Quitar todas' : 'Seleccionar todas'}
              </button>
            )}
          </div>
          <div className="border border-divider rounded-lg max-h-64 overflow-y-auto scrollbar-slim divide-y divide-divider">
            {activeClients.length === 0 ? (
              <p className="px-3 py-4 text-sm text-content-subtle text-center">
                No hay empresas activas en la cartera.
              </p>
            ) : (
              activeClients.map((c) => (
                <label key={c.tenant_schema}
                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-soft cursor-pointer">
                  <input type="checkbox" checked={selected.includes(c.tenant_schema)}
                    onChange={() => toggle(c.tenant_schema)}
                    className="rounded border-divider text-primary focus:ring-focus-ring" />
                  <span className="flex-1 min-w-0">
                    <span className="text-sm text-content">{c.tenant_name}</span>
                    <span className="block text-xs text-content-subtle">{c.tenant_schema}</span>
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}
